import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppSidebar } from "./Sidebar";
import Calendar from "./Calendar";
import { ChatInterface } from "./ChatInterface";
import Settings from "./Settings";
import ArticleView from "./ArticleView";
import ArticleListView from "./ArticleListView";
import { useAuth0 } from "@auth0/auth0-react";
import { ToastContainer } from "react-toastify";
import "../Styles/ArticleListView.css";
import Logo from "../Images/logo.png";
import useUserStore from "../store";
import ArticleCreator from "./CreateYourOwnArticle";

export default function MainPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth0();
  const [hasBlogs, setHasBlogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setUserId } = useUserStore();
  const [activePage, setActivePage] = useState("accountSetup");
  // useEffect for setting the active page based on the current route.
  useEffect(() => {
    const pathToPage = {
      "/calendar": "contentCalendar",
      "/integrations": "integrations",
      "/settings": "settings",
      "/onboarding": "accountSetup",
      "/articles-table-view": "articlesTableView",
      "/create-article": "createArticle",  // Add this line to handle the create-article route
    };
    // Only set activePage for known paths, otherwise let the switch in renderContent handle it
    if (pathToPage[location.pathname]) {
      setActivePage(pathToPage[location.pathname]);
    }
  }, [location.pathname]);
  // useEffect for checking user existence and blog setup.
  useEffect(() => {
    if (user) {
      const checkUser = async () => {
        try {
          const userExistsResponse = await fetch(
            `https://trendhopper-c3934b7293e6.herokuapp.com/users/exist/${user.email}/`
          );
          const userExistsData = await userExistsResponse.json();
          setHasBlogs(userExistsData.hasBlogs);
          setUserId(userExistsData.user_id);
          localStorage.setItem("user_id", userExistsData.user_id);
        } catch (error) {
          console.error("Error checking user or blogs:", error);
        } finally {
          setLoading(false);
        }
      };
      checkUser();
    }
  }, [user, setUserId]);
  // useEffect for navigation based on blog setup status.
  useEffect(() => {
    if (loading) {
      return;
    }
    // IMPORTANT CHANGE: Re-check user data if hasBlogs is false and we're not on onboarding
    const recheckUser = async () => {
        try {
            const userExistsResponse = await fetch(
                `https://trendhopper-c3934b7293e6.herokuapp.com/users/exist/${user.email}/`
            );
            const userExistsData = await userExistsResponse.json();
            setHasBlogs(userExistsData.hasBlogs); // Update hasBlogs with the new value
            // Navigate based on the *new* hasBlogs value
            if (userExistsData.hasBlogs === false && location.pathname !== "/onboarding") {
                navigate("/onboarding");
            }
            if (userExistsData.hasBlogs === true && location.pathname === "/onboarding") {
                navigate("/articles-table-view");
            }
        } catch (error) {
            console.error("Error re-checking user or blogs:", error);
        }
    };
    if (hasBlogs === false && location.pathname !== "/onboarding") {
      recheckUser(); // Call the re-check function
    } else if (hasBlogs === true && location.pathname === "/onboarding") {
        navigate("/articles-table-view");
    }
  }, [hasBlogs, loading, location.pathname, navigate, user]); // Add user to dependency array
  // Function to handle navigation to different pages.
  const handleNavigation = (page) => {
    if (hasBlogs === false) {
      navigate("/onboarding");
      return;
    }
    const pageToPath = {
      contentCalendar: "/calendar",
      integrations: "/integrations",
      settings: "/settings",
      accountSetup: "/onboarding",
      articlesTableView: "/articles-table-view",
      createArticle: "/create-article",  // Add this line for the "Create Article" page

    };
    navigate(pageToPath[page]);
  };
  // Function to handle article clicks and navigate to the article view.
  const handleArticleClick = (articleId) => {
    if (hasBlogs === false) {
      navigate("/onboarding");
      return;
    }
    navigate(`/article/${articleId}`);
  };

  // Function to render content based on the current route and application state.
  const renderContent = () => {
    if (loading) {
      return (
        <div className="full-page-spinner flex flex-col items-center justify-center min-h-screen bg-gray-900">
          <div className="relative w-24 h-24">
            <img
              src={Logo}
              alt="Trendhopper Logo"
              className="absolute inset-0 m-auto w-12 h-12"
            />
            <div className="w-full h-full border-4 border-t-purple-500 border-r-transparent border-b-gray-600 border-l-gray-600 rounded-full animate-spin"></div>
          </div>
          <p className="text-white mt-4 text-center">Loading Trendhopper...</p>
        </div>
      );
    }

    //  Handle the onboarding case separately
    if (hasBlogs === false) {
      return <ChatInterface />;
    }

    // Use location.pathname for routing, handle dynamic routes correctly
    if (location.pathname.startsWith("/article/")) {
      return <ArticleView />;
    }
    console.log("Current Path:", location.pathname); // Log current path

    // Handle routing for dynamic content
    switch (location.pathname) {
      case "/calendar":
        return <Calendar onArticleClick={handleArticleClick} />;
      case "/settings":
        return <Settings />;
      case "/articles-table-view":
        return <ArticleListView />;
      case "/create-article":
        return <ArticleCreator />;  // Render the ArticleCreator component directly
      case "/onboarding":
          return <ChatInterface />;
      default:
        console.log("No specific route matched, no default case needed.");
    }
    switch (activePage) {
      //This should be outside of the location.pathname
      case "contentCalendar":
        return <Calendar onArticleClick={handleArticleClick} />;
      case "settings":
        return <Settings />;
      case "articlesTableView":
        return <ArticleListView />;
      case "accountSetup":
        return <ChatInterface />;
      default:
        return <ChatInterface />; // Or some other fallback
    }
  };


  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {location.pathname !== "/onboarding" && (
        <AppSidebar onSelect={handleNavigation} activePage={activePage} />
      )}
      <main className="flex-1 overflow-auto flex flex-col">
        <div className={`${location.pathname.startsWith("/article/") ? "" : "p-8"} h-full`}>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
